'use client'

import { useRouter } from 'next/navigation'
import { createContext, useContext, useEffect, useState } from 'react'

type Locale = 'vi' | 'en'

interface I18nContextType {
  locale: Locale
  changeLocale: (locale: Locale) => void
  t: (key: string, options?: any) => string
}

const I18nContext = createContext<I18nContextType | undefined>(undefined)

export const useI18n = () => {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider')
  }
  return context
}

interface I18nProviderProps {
  children: React.ReactNode
  locale?: Locale
}

// Helper functions for localStorage
const getStoredLocale = (): Locale => {
  if (typeof window === 'undefined') return 'vi'

  try {
    const stored = localStorage.getItem('app-locale')
    return stored === 'en' || stored === 'vi' ? stored : 'vi'
  } catch (error) {
    console.error('Error reading locale from localStorage:', error)
    return 'vi'
  }
}

const setStoredLocale = (locale: Locale): void => {
  if (typeof window === 'undefined') return

  try {
    localStorage.setItem('app-locale', locale)
  } catch (error) {
    console.error('Error writing locale to localStorage:', error)
  }
}

// Utility function to get current locale from localStorage
export const getCurrentLocale = (): Locale => {
  return getStoredLocale()
}

// SSR fallback: import tiếng Việt nếu không có translations
let viTranslations: any = {}
if (typeof window === 'undefined') {
  try {
    viTranslations = require('../../../public/locales/vi/common.json')
  } catch (e) {
    viTranslations = {}
  }
}

export const I18nProvider = ({ children, locale: initialLocale }: I18nProviderProps) => {
  const [locale, setLocale] = useState<Locale>(() => {
    // Use initialLocale if provided, otherwise get from localStorage, default to 'vi'
    return initialLocale || getStoredLocale()
  })
  const [translations, setTranslations] = useState<any>({})
  const router = useRouter()

  useEffect(() => {
    // Load translations based on locale
    const loadTranslations = async () => {
      try {
        const response = await fetch(`/locales/${locale}/common.json`)
        const data = await response.json()
        setTranslations(data)
      } catch (error) {
        console.error('Failed to load translations:', error)
      }
    }

    loadTranslations()
  }, [locale])

  const changeLocale = (newLocale: Locale) => {
    setLocale(newLocale)
    setStoredLocale(newLocale)
    // Update URL with new locale
    const currentPath = window.location.pathname
    const newPath = currentPath.replace(/^\/(vi|en)/, `/${newLocale}`)
    router.push(newPath)
  }

  const t = (key: string, options?: any) => {
    const keys = key.split('.')
    let value = translations

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k]
      } else {
        // SSR: fallback sang tiếng Việt nếu có
        if (typeof window === 'undefined') {
          let viValue = viTranslations
          for (const k2 of keys) {
            if (viValue && typeof viValue === 'object' && k2 in viValue) {
              viValue = viValue[k2]
            } else {
              return key
            }
          }
          if (typeof viValue === 'string') return viValue
        }
        return key
      }
    }

    if (typeof value === 'string') {
      // Simple interpolation for {{variable}}
      if (options) {
        return value.replace(/\{\{(\w+)\}\}/g, (match, key) => {
          return options[key] || match
        })
      }
      return value
    }

    return key
  }

  return <I18nContext.Provider value={{ locale, changeLocale, t }}>{children}</I18nContext.Provider>
}
